import { onScopeDispose, ref, watch, type Ref } from 'vue'

/**
 * Mirrors `source` into `debounced` after `debounceMs` of inactivity. Call `flush()` to apply immediately (e.g. range `change`).
 */
export function useDebouncedRef<T>(source: Ref<T>, debounceMs: number) {
  const debounced = ref(source.value) as Ref<T>
  let timer: ReturnType<typeof setTimeout> | null = null

  watch(
    source,
    () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        debounced.value = source.value
        timer = null
      }, debounceMs)
    },
    { flush: 'post' }
  )

  function flush() {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    debounced.value = source.value
  }

  onScopeDispose(() => {
    if (timer) clearTimeout(timer)
  })

  return { debounced, flush }
}
